import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ProductModalLineFragment,
  ProductModalSupplierFragment,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { Input as FormatProductInput } from '@/modules/requisitions/utils/formatProductItem'
import Button from '@/modules/shared/components/button/Button'
import { Modal } from '@/modules/shared/components/modal/ModalComponent'
import { PPNewDetailLink } from '@/modules/shared/components/ppnew-detail-link/PPNewDetailLink'
import ProductModalBarcodeDetails from '@/modules/shared/components/product-modal/ProductModalBarcodeDetails'
import ProductModalProductDetails from '@/modules/shared/components/product-modal/ProductModalProductDetails'
import ProductModalProductPreview from '@/modules/shared/components/product-modal/ProductModalProductPreview'
import ProductModalProductTags from '@/modules/shared/components/product-modal/ProductModalProductTags'
import ProductModalSubstituteProducts from '@/modules/shared/components/product-modal/ProductModalSubstituteProducts'
import ProductModalSupplierDeliveryFees from '@/modules/shared/components/product-modal/ProductModalSupplierDeliveryFees'
import ProductModalSupplierDetails from '@/modules/shared/components/product-modal/ProductModalSupplierDetails'
import { useGetProductModal } from '@/modules/shared/components/product-modal/hooks/useGetProductModal'
import { classNames } from '@/modules/shared/utils/classNames'
import { formatProductName } from '@/modules/shared/utils/formatProductName'

interface ProductModalProps {
  showModal: boolean
  setShowModal: (value: boolean) => void
  line?: ProductModalLineFragment | null
  supplier?: ProductModalSupplierFragment | null
  formatProduct?: (input: FormatProductInput) => string
  productId: number
  image?: string | null
}

export default function ProductModal({
  showModal,
  setShowModal,
  supplier,
  formatProduct,
  productId,
  line,
  image,
}: ProductModalProps) {
  const { t } = useTranslation()
  const { currentOrganisation, user, currentLegalEntity } = useSessionUser()
  const { product, loadingStates, error } = useGetProductModal({
    productId,
    skip: !showModal,
    supplierId: supplier?.id,
  })
  const { brand, itemDescription, id, owner } = product || {}

  const onCloseModal = () => setShowModal(false)

  const showManageProductLink = useMemo(() => {
    return (
      user?.isAdmin || owner?.id === currentLegalEntity?.id || owner?.id === currentOrganisation?.id || owner?.id === 1
    )
  }, [user, owner, currentLegalEntity, currentOrganisation])

  return (
    <Modal showModal={showModal} onCloseModal={onCloseModal} loading={loadingStates.loading} error={error}>
      <Modal.Panel
        data-testid="product-details-modal"
        className="flex h-[80vh] w-full flex-col overflow-hidden rounded-md bg-white shadow-xl transition-all md:max-w-[56.25rem]"
      >
        <Modal.Title title={formatProductName(brand || null, itemDescription || null)} onCloseModal={onCloseModal} />

        <Modal.Body className="overflow-y-scroll">
          <>
            <ProductModalProductPreview
              product={product}
              formatProduct={formatProduct}
              line={line}
              supplier={supplier}
              image={image}
            />
            <ProductModalProductDetails line={line} product={product} supplier={supplier} />
            <ProductModalProductTags tagCount={product?.productTags?.totalCount || 0} productId={productId} />
            {supplier?.supplierRelationship?.id && (
              <>
                <ProductModalSupplierDetails tradeRelationshipId={supplier.supplierRelationship?.id} />
                <ProductModalSupplierDeliveryFees tradeRelationshipId={supplier.supplierRelationship?.id || 0} />
              </>
            )}
            <ProductModalBarcodeDetails productId={productId} barcodesTotalCount={product?.barcodes?.totalCount || 0} />
            {!currentOrganisation?.isASupplier && (
              <ProductModalSubstituteProducts
                productId={productId}
                substituteProductsTotalCount={product?.substituteProducts?.totalCount || 0}
              />
            )}
          </>
        </Modal.Body>
        <Modal.Footer
          className={classNames('flex flex-col gap-2 md:flex-row md:items-center', {
            'justify-between': showManageProductLink,
            'justify-end': !showManageProductLink,
          })}
        >
          {showManageProductLink && (
            <PPNewDetailLink
              type="product"
              id={String(id)}
              openNewTab
              className="flex h-11 w-full items-center justify-center rounded-md bg-gray-200 px-5 text-center text-sm md:w-fit"
            >
              {t('general.manageProduct', 'Manage Product')}
            </PPNewDetailLink>
          )}
          <Button className="h-11 w-full rounded-md bg-gray-200 px-5 text-sm md:w-fit" onClick={onCloseModal}>
            {t('general.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal.Panel>
    </Modal>
  )
}
